import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const TOTAL_SECONDS = 30 * 60;      // 30 minutes total session
const IDLE_SHOW_AFTER = 30;         // Show timer after 30 seconds of inactivity

export default function useInactivityLogout() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [secondsLeft, setSecondsLeft] = useState(TOTAL_SECONDS);
  const [isIdle, setIsIdle] = useState(false);

  const lastActivityRef = useRef(Date.now());
  const intervalRef = useRef(null);
  const isLoggingOut = useRef(false);

  // Only for admin and worker
  const isApplicable = user?.role === 'admin' || user?.role === 'worker';

  const doLogout = useCallback(async () => {
    if (isLoggingOut.current) return;
    isLoggingOut.current = true;
    clearInterval(intervalRef.current);
    await logout();
    navigate('/login');
  }, [logout, navigate]);

  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    setIsIdle(false);
    setSecondsLeft(TOTAL_SECONDS);
  }, []);

  useEffect(() => {
    if (!isApplicable || !user) return;

    // Activity events
    const events = ['click', 'keydown', 'mousedown', 'touchstart', 'scroll'];
    let lastReset = 0;

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastReset > 1000) {
        lastReset = now;
        resetActivity();
      }
    };

    events.forEach(e => window.addEventListener(e, handleActivity, { passive: true }));

    // Tick every second
    intervalRef.current = setInterval(() => {
      const idleSeconds = Math.floor((Date.now() - lastActivityRef.current) / 1000);
      const remaining = Math.max(0, TOTAL_SECONDS - idleSeconds);

      setSecondsLeft(remaining);
      setIsIdle(idleSeconds >= IDLE_SHOW_AFTER);

      if (remaining <= 0) {
        doLogout();
      }
    }, 1000);

    return () => {
      clearInterval(intervalRef.current);
      events.forEach(e => window.removeEventListener(e, handleActivity));
    };
  }, [isApplicable, user, resetActivity, doLogout]);

  return { secondsLeft, isIdle };
}
